import { useMemo } from 'react';

import type { Suggestions } from '@mint-sdk/api-recommendations';
import { useAtom } from 'jotai';

import { SUGGESTION_FILTERS } from '../components/organisms/SuggestionFilter/helpers.js';
import { useSuggestionFilterQuery } from '../queries/suggestions.js';
import { suggestionSelectedFiltersAtom } from '../store/index.js';

export const useSuggestionFilter = () => {
  const [selectedSuggestionFilter, setSuggestionFilter] = useAtom(
    suggestionSelectedFiltersAtom,
  );

  const {
    data: filterOptions,
    isLoading,
    isPending,
  } = useSuggestionFilterQuery(selectedSuggestionFilter);

  const intersectedFilters = useMemo(() => {
    if (!selectedSuggestionFilter || !filterOptions) return {};

    return SUGGESTION_FILTERS.reduce<Record<string, string[]>>((acc, key) => {
      const selected = selectedSuggestionFilter[key]?.[0];
      const options = filterOptions[key];

      if (!selected || !options?.length) return acc;

      const intersected = selected
        .split(',')
        .filter((item) => options.includes(item));

      if (intersected.length) {
        acc[key] = intersected;
      }

      return acc;
    }, {});
  }, [selectedSuggestionFilter, filterOptions]);

  const intersectedSuggestionFilters = useMemo(() => {
    return SUGGESTION_FILTERS.reduce<typeof selectedSuggestionFilter>(
      (acc, key) => {
        const values = intersectedFilters[key];

        acc[key] = values?.length
          ? [values.join(',')]
          : selectedSuggestionFilter[key];

        return acc;
      },
      { ...selectedSuggestionFilter },
    );
  }, [selectedSuggestionFilter, intersectedFilters]);

  const transformToOptions = (
    data: Suggestions.GetFilterResponseType | undefined,
  ) => {
    if (!data) return;
    return {
      ...data,
      campaigns: data.campaigns.map((el: string) => ({
        value: el,
        label: el,
      })),
      channels: data.channels.map((el: string) => ({ value: el, label: el })),
      platforms: data.platforms.map((el: string) => ({
        value: el,
        label: el,
      })),
    };
  };

  return {
    filterOptions: transformToOptions(filterOptions),
    isFilterLoading: isLoading || isPending,
    selectedSuggestionFilter: intersectedSuggestionFilters,
    setSuggestionFilter,
  };
};
